import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {URLS} from '../../API/API endpoints'
import moment from "moment";
import { DatePicker } from "antd";
import { justifyContent } from "@mui/system";
import LoadingSpinner from '../CustomWidgets/Laoding'
/* ─────────────────────────────────────────────
   Sub-components
───────────────────────────────────────────── */

function PhoneInput({ label, value, onChange, error, inputRef }) {
  const { i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  return (
    <div style={styles.formGroup}>
      <label style={styles.label}>{label}</label>
      <div
        style={{
          ...styles.inputWrap,
          flexDirection: isAr ? "row-reverse" : "row",
        }}
      >
        <span style={styles.prefix}>{isAr ? "20+" : "+20"}</span>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={onChange}
          style={{
            ...styles.input,
            textAlign: 'left',
            paddingLeft: 45,
            paddingRight:  12,
            borderColor: error ? "#E24B4A" : "#d1d5db",
          }}
          maxLength={11}
          autoComplete="off"
          autoFocus
        />
      </div>
      {error && <span style={styles.errorText}>{error}</span>}
    </div>
  );
}

function TextInput({
  label,
  value,
  onChange,
  placeholder,
  readOnly,
  error,
  inputRef,
  onKeyDown,
}) {
  return (
    <div style={styles.formGroup}>
      <label style={styles.label}>{label}</label>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        readOnly={readOnly}
        style={{
          ...styles.input,
          paddingLeft: 14,
          paddingRight: 14,
          backgroundColor: readOnly ? "#f9fafb" : "#fff",
          borderColor: error ? "#E24B4A" : "#d1d5db",
          cursor: readOnly ? "not-allowed" : "text",
        }}
        autoComplete="off"
      />
      {error && <span style={styles.errorText}>{error}</span>}
    </div>
  );
}

function AddressInput({ label, value, onChange, placeholder }) {
  return (
    <div style={styles.formGroup}>
      <label style={{ ...styles.label, fontSize: 11 }}>{label}</label>
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{ ...styles.input, paddingLeft: 14,paddingRight: 14, fontSize: 13 }}
        autoComplete="off"
      />
    </div>
  );
}

/* ─────────────────────────────────────────────
   Address type config
───────────────────────────────────────────── */
const ADDRESS_TYPES = [
  { id: 1, key: "Villa", icon: "🏠" },
  { id: 2, key: "Office", icon: "🏢" },
  { id: 3, key: "Apartment", icon: "🏗️" },
];

const ADDRESS_FIELDS = {
  1: [
    {
      key: "addField1",
      labelKey: "House No. / Villa No.",
      placeholder: "e.g. 42",
    },
    { key: "addField2", labelKey: "Group No.", placeholder: "e.g. G-7" },
    { key: "addField3", labelKey: "Street", placeholder: "Street name" },
    {
      key: "addField4",
      labelKey: "Additional Info",
      placeholder: "Landmark or notes",
    },
  ],
  2: [
    { key: "addField1", labelKey: "Company", placeholder: "Company Name" },
    { key: "addField2", labelKey: "Floor", placeholder: "e.g. 3rd" },
    {
      key: "addField3",
      labelKey: "Building",
      placeholder: "Building name / no.",
    },
    {
      key: "addField4",
      labelKey: "Additional Info",
      placeholder: "Landmark or notes",
    },
  ],
  3: [
    {
      key: "addField1",
      labelKey: "Building & Group",
      placeholder: "e.g. B2-G4",
    },
    { key: "addField2", labelKey: "Apartment No.", placeholder: "e.g. 12A" },
    { key: "addField3", labelKey: "Floor", placeholder: "e.g. 5th" },
    {
      key: "addField4",
      labelKey: "Additional Info",
      placeholder: "Landmark or notes",
    },
  ],
};

/* ─────────────────────────────────────────────
   Main Modal
───────────────────────────────────────────── */
function AddCustomerModal({
  onClose,
  confirm,
  showSkipButton,
  isAddressMandatory,
  editData,
  onUpdate,
  loading
}) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";

  const { user_id } = useSelector((state) => state?.login?.user?.result);
  const paymentReducer = useSelector((state) => state?.paymentReducer);

  const inputRef = useRef(null);

  /* form state */
  const [mobile, setMobile] = useState(editData ? editData?.phone : "");
  const [displayName, setDisplayName] = useState(editData ? editData?.name : "");
  const [addField1, setaddField1] =useState(editData ? editData?.addresses[0]?.add_field_1 : "");
  const [addField2, setaddField2] =useState(editData ? editData?.addresses[0]?.add_field_2 : "");
  const [addField3, setaddField3] =useState(editData ? editData?.addresses[0]?.add_field_3 : "");
  const [addField4, setaddField4] = useState(editData ? editData?.addresses[0]?.add_field_4 : "");
  const [creditDate, setCreditDate] = useState(editData ? editData?.mobile : "");
  const [creditAmount, setCreditAmount] = useState(editData ? editData?.credit_amount : "");
  /* UI state */
  const [selectedAddressType, setSelectedAddressType] = useState(editData ? { id: editData?.addresses[0]?.type_id, name: editData?.addresses[0]?.name } : null);
  const [addressTypes, setAddressTypes] = useState(
    ADDRESS_TYPES.map((a) => ({ id: a.id, name: t(a.key) })),
  );
  const [searchMode, setSearchMode] = useState("mobile");
  const [customerExist, setCustomerExist] = useState(false);
  const [partnerId, setPartnerId] = useState("");
  const [inputDisabled, setInputDisabled] = useState(false);
  const [errors, setErrors] = useState({});

  /* payment method */
  let paymentMethod = null;
  try {
    paymentMethod =
      typeof paymentReducer === "string"
        ? JSON.parse(paymentReducer).selectedPaymentMethod
        : paymentReducer?.selectedPaymentMethod;
  } catch (_) {}

  const addressFields = selectedAddressType
    ? ADDRESS_FIELDS[selectedAddressType.id]
    : [];
  const fieldSetters = {
    addField1: setaddField1,
    addField2: setaddField2,
    addField3: setaddField3,
    addField4: setaddField4,
  };
  const fieldValues = { addField1, addField2, addField3, addField4 };

  const mobileLen = /^0/.test(mobile) ? 11 : 10;
  const isAddressEmpty = !addField1 && !addField2 && !addField3 && !addField4;

  const canConfirm =
    mobile.length >= 10 &&
    displayName.length > 0 &&
    mobile.length === mobileLen &&
    !(paymentMethod?.name === "Delivery" && isAddressEmpty);

  const CreateCustomer = async (values) => {
      confirm({
        userName: values.name,
        mobile: values.mobile.replace(/^0+/, ""),
        address_type_id: values.address_type,
        add_field_1: values.addField1,
        add_field_2: values.addField2,
        add_field_3: values.addField3,
        add_field_4: values.addField4,
        credit_amount:values.credit_amount,
        credit_as_on_date:values.credit_as_on_date,
      });   
  };

  const UpdateCustomer = async (values) => {
      onUpdate({
        customer_id:editData?.id,
        userName: values.name,
        mobile: values.mobile.replace(/^0+/, ""),
        address_type_id: values.address_type,
        add_field_1: values.addField1,
        add_field_2: values.addField2,
        add_field_3: values.addField3,
        add_field_4: values.addField4,
        credit_amount:values.credit_amount,
        credit_as_on_date:values.credit_as_on_date,
      });   
  };


  /* ── handlers ── */
  const handleConfirm = async () => {
    const errs = {};
    if (mobile.length < 10)
      errs.mobile = t("Please enter a valid phone number");
    if (isAddressMandatory && !selectedAddressType?.id)
      errs.address = t("Please select an address type");
    if (selectedAddressType?.id && (!addField1 || !addField2 || !addField3))
      errs.addressFields = t("Please fill in all required address fields");
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    // setLoading(true);
    try {
      const values = {
        mobile,
        name: displayName,
        address_type: selectedAddressType?.id,
        addField1,
        addField2,
        addField3,
        addField4,
        credit_amount:creditAmount,
        credit_as_on_date:creditDate,

      };
      if(editData?.id){
await UpdateCustomer(values);
      }else{
await CreateCustomer(values);
      }
    } catch (e) {
      console.error(e);
    }
    // setLoading(false);
  };

  const handleSuggestionClick = (customer) => {
    setMobile(customer.phone || "");
    setDisplayName(customer.name || "");
    setCustomerExist(true);
    setPartnerId(customer.id);
    const addresses = customer.addresses || [];
    if (addresses.length > 0 && addresses[0]?.type_id !== false) {
      setAddressTypes(
        addresses.map((a) => ({ id: a.type_id, name: t(a.name) })),
      );
      const def = addresses[0];
      setSelectedAddressType({ id: def.type_id, name: def.name });
      setaddField1(def.add_field_1 || "");
      setaddField2(def.add_field_2 || "");
      setaddField3(def.add_field_3 || "");
      setaddField4(def.add_field_4 || "");
    } else {
      setAddressTypes(ADDRESS_TYPES.map((a) => ({ id: a.id, name: t(a.key) })));
      setaddField1("");
      setaddField2("");
      setaddField3("");
      setaddField4("");
    }
    setInputDisabled(true);
  };

  const clearData = () => {
    setMobile("");
    setDisplayName("");
    setCustomerExist(false);
    setPartnerId("");
    setaddField1("");
    setaddField2("");
    setaddField3("");
    setaddField4("");
    setAddressTypes(ADDRESS_TYPES.map((a) => ({ id: a.id, name: t(a.key) })));
    setSelectedAddressType(null);
    setInputDisabled(false);
  };

  const handleSkip = () => {
    // confirm({ skip: true });
    onClose();
  };


  useEffect(() => {
    inputRef.current?.focus();
  }, []);


  const handleDateChange = (date, dateString) => {
    setCreditDate(dateString);
  };

  /* ─────────────────────────────────────────────
     Render
  ───────────────────────────────────────────── */
  return (
    <>
    {loading && <LoadingSpinner />}
     <div
      style={styles.overlay}

      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{ ...styles.modal, direction: isAr ? "rtl" : "ltr" }}
      className=" scrollbar-none"
      >
        {/* ── Header ── */}
        <div style={styles.header}
        >
          <div className="flex items-center gap-x-2">
          <div style={styles.headerIcon}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#2563eb"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <div>
            <h2 style={styles.headerTitle}>{editData ? t("Update Customer") : t("Add Customer")}</h2>
          </div>
          </div>
          <button onClick={onClose} style={styles.closeBtn} title="Close">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* ── Body ── */}
        <div style={styles.body}>
          {/* Contact section */}
          <div style={styles.sectionHeader}>
            <span style={styles.sectionBadge}>1</span>
            <span style={styles.sectionTitle}>{t("Contact details")}</span>
          </div>

          <div style={styles.row}>
            <PhoneInput
              inputRef={inputRef}
              label={t("Phone Number")}
              value={mobile}
              error={errors.mobile}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "");
                const max = /^0/.test(v) ? 11 : 10;
                if (v.length <= max) {
                  setMobile(v);
                }
              }}
            />
            <TextInput
              label={t("Full name")}
              value={displayName}
              error={errors.name}
              readOnly={false}
              onChange={(e) => {
                setDisplayName(e.target.value);
              }}
              placeholder={t("Customer name")}
            />
          </div>

{!editData?.is_credit_record_available && 
<>
     <div style={styles.divider} />
          {/* ── Credit Details Section ── */}
          <div style={styles.sectionHeader}>
            <span style={styles.sectionBadge}>2</span>
            <span style={styles.sectionTitle}>{t("Credit details")}</span>
          </div>
           <div style={styles.formGroup}>
            <div style={styles.row}>
               <div style={styles.formGroup}>
                <label style={styles.label}>{t("Date")}</label>
               <DatePicker
                      style={{ width: "100%", borderColor:"#d1d5db" }}
                      onChange={handleDateChange}
                      placeholder={t("Select Date")}
                      disabledDate={(current) =>
                        current && current > moment().endOf("day")
                      }
                      className="h-10 "
                       getPopupContainer={(trigger) => trigger.parentNode}
                    />
              </div>
            
                <TextInput
                  label={t("Amount")}
                  value={creditAmount}
                  readOnly={editData && editData?.credit_amount && editData?.credit_amount > 0}
                   backgroundColor={
                        (editData &&
                          editData?.credit_amount &&
                          editData?.credit_amount > 0
                            ? "bg-gray-100"
                            : null)
                      }
                  error={errors.name}
                  onChange={(e) => {
                    let val = e.target.value
                      .replace(/[^0-9.]/g, "")
                      .replace(/(\..*?)\..*/g, "$1");

                    if (val.length > 10) return;

                    setCreditAmount(val);
                  }}
                  placeholder={t("Enter Amount")}
                />
                </div>
              </div></>}
     

          

          <div style={styles.divider} />

          {/* Address section */}
          <div style={styles.sectionHeader}>
            <span style={styles.sectionBadge}>3</span>
            <span style={styles.sectionTitle}>{t("Address")}</span>
            {!isAddressMandatory && (
              <span style={styles.optionalTag}>{t("optional")}</span>
            )}
            {errors.address && (
              <span style={{ ...styles.errorText, marginLeft: 8 }}>
                {errors.address}
              </span>
            )}
          </div>

          <div style={styles.addressTabs}>
            {addressTypes.map((addr) => {
              const icon =
                ADDRESS_TYPES.find((a) => a.id === addr.id)?.icon || "📍";
              const isActive = selectedAddressType?.id === addr.id;
              return (
                <button
                  key={addr.id}
                  onClick={() => {
                    if (isActive) {
                      setSelectedAddressType(null);
                      return;
                    }
                    setSelectedAddressType(addr);
                    if (!customerExist) {
                      setaddField1("");
                      setaddField2("");
                      setaddField3("");
                      setaddField4("");
                    }
                  }}
                  style={{
                    ...styles.tabBtn,
                    ...(isActive ? styles.tabBtnActive : {}),
                  }}
                >
                  <span style={{ fontSize: 15 }}>{icon}</span>
                  {t(addr.name)}
                </button>
              );
            })}
          </div>

          {/* Address fields */}
          {selectedAddressType && (
            <div style={styles.addressGrid}>
              {errors?.addressFields && (
                <div style={{ gridColumn: "1/-1", ...styles.errorBanner }}>
                  {errors?.addressFields}
                </div>
              )}
              {addressFields?.map((field) => (
                <AddressInput
                  key={field.key}
                  label={t(field.labelKey)}
                  placeholder={t(field.placeholder)}
                  value={fieldValues[field.key]}
                  onChange={(e) => fieldSetters[field.key](e.target.value)}
                />
              ))}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={styles.footer}>
          {showSkipButton && (
            <button onClick={handleSkip} style={styles.skipBtn}>
              {t("Cancel")}
            </button>
          )}
          <button
            onClick={handleConfirm}
            disabled={!canConfirm || loading}
            style={{
              ...styles.confirmBtn,
              opacity: canConfirm && !loading ? 1 : 0.45,
            }}
          >
            {loading ? (
              <span style={styles.spinner} />
            ) : (
              <>
                {t("Confirm")}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
    </>
   
  );
}

/* ─────────────────────────────────────────────
   Styles
───────────────────────────────────────────── */
const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.55)",
    backdropFilter: "blur(6px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 999,
    padding: 16,
  },
  modal: {
    background: "#fff",
    borderRadius: 20,
    boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
    width: "100%",
    maxWidth: 680,
    maxHeight: "90vh",
    overflowY: "auto",
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      zoom: 1.30,   // or whatever compensates

  },

  /* header */
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent:"space-between",
    gap: 12,
    padding: "22px 28px 20px",
    borderBottom: "1px solid #f1f5f9",
    position: "sticky",
    top: 0,
    // background: "#fff",
    zIndex: 1,
    borderRadius: "20px 20px 0 0",
  },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: "#eff6ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  headerTitle: { fontSize: 16, fontWeight: 600, color: "#0f172a", margin: 0 },
  headerSubtitle: { fontSize: 15, color: "#64748b", margin: "2px 0 0" },
  closeBtn: {
    marginInlineStart: "auto",
    width: 32,
    height: 32,
    borderRadius: 8,
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#64748b",
    flexShrink: 0,
    transition: "all 0.15s",
  },

  /* body */
  body: { padding: "20px 24px 4px" },

  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 14,
  },
  sectionBadge: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    background: "#1b4597",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  sectionTitle: { fontSize: 13, fontWeight: 600, color: "#1e293b" },
  optionalTag: {
    fontSize: 11,
    color: "#94a3b8",
    background: "#f1f5f9",
    borderRadius: 20,
    padding: "1px 8px",
  },

  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 14,
    marginBottom: 14,
  },

  formGroup: { display: "flex", flexDirection: "column", gap: 5 },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  inputWrap: { display: "flex", alignItems: "center", position: "relative" },
  prefix: {
    position: "absolute",
    left: 14,
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    pointerEvents: "none",
    zIndex: 1,
  },
  input: {
    width: "100%",
    height: 46,
    fontSize: 16,
    border: "1.5px solid #e2e8f0",
    borderRadius: 10,
    outline: "none",
    color: "#0f172a",
    fontFamily: "inherit",
    transition: "border-color 0.15s, box-shadow 0.15s",
    background: "#fff",
  },
  errorText: { fontSize: 11, color: "#dc2626", marginTop: 2 },
  errorBanner: {
    fontSize: 12,
    color: "#dc2626",
    background: "#fef2f2",
    borderRadius: 8,
    padding: "8px 12px",
  },

  clearBtn: {
    fontSize: 12,
    color: "#2563eb",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontWeight: 500,
  },

  createNew: {
    padding: "10px 14px",
    fontSize: 13,
    color: "#2563eb",
    fontWeight: 500,
    cursor: "pointer",
    background: "#f8fafc",
  },

  divider: { height: 1, background: "#f1f5f9", margin: "16px 0" },

  addressTabs: { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 },
  tabBtn: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    padding: "8px 16px",
    borderRadius: 10,
    border: "1.5px solid #e2e8f0",
    background: "#f8fafc",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.15s",
    fontFamily: "inherit",
  },
  tabBtnActive: {
    background: "#eff6ff",
    borderColor: "#2563eb",
    color: "#1d4ed8",
  },

  addressGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    background: "#f8fafc",
    borderRadius: 12,
    padding: 14,
    border: "1px solid #f1f5f9",
    marginBottom: 4,
  },

  /* footer */
  footer: {
    display: "flex",
    gap: 10,
    padding: "20px 24px 20px",
    borderTop: "1px solid #f1f5f9",
    alignItems: "center",
  },
  skipBtn: {
    flex: 1,
    height: 48,
    borderRadius: 10,
    border: "1.5px solid #e2e8f0",
    background: "#f8fafc",
    color: "#64748b",
    fontSize: 15,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  confirmBtn: {
    flex: 2,
    height: 48,
    borderRadius: 10,
    border: "none",
    background: "#1b4597",
    color: "#fff",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "inherit",
    transition: "background 0.15s, opacity 0.15s",
  },
  spinner: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    border: "2.5px solid rgba(255,255,255,0.3)",
    borderTopColor: "#fff",
    display: "inline-block",
    animation: "spin 0.7s linear infinite",
  },
  creditBox: {
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    marginBottom: 16,
    overflow: "hidden",
  },

  creditHeader: {
    background: "#C9ECF7",
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 600,
    color: "#272728",
  },

  creditBody: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 14,
    padding: "16px",
  },
};

export default AddCustomerModal;
